import { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { sum } from 'lodash';
import {
  MRT_ColumnOrderState,
  MRT_VisibilityState,
} from 'material-react-table';
import { useQuery } from 'react-query';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { MonitorFundButton } from 'src/components/MonitorFundButton';
import { useAuthContext } from 'src/context/AuthContextProvider';
import {
  getIndicatorsColumnsByMarketAndValueType,
  getOperationComposition,
  getOperationIndicators,
  getOperationViewMetadata,
} from 'src/services/wallet';
import { Operation } from 'src/types/operation';
import { CompositionDataItem } from 'src/types/wallet';
import { UQBAR_DATE_FORMAT, UQBAR_TIMEZONE } from 'src/utils/date';
import { buildLoginRedirectPath } from 'src/utils/url';

import { FundViewInfoCards } from './components/FundViewInfoCards';
import { useMonitorContext } from '../../MonitorContextProvider';
import TabNavigation from '../../components/TabNavigation';
import { DynamicIndicatorsTable } from '../components/FundIndicatorsTable';
import { IndicatorDetailsModal } from '../components/IndicatorDetailsModal';
import { ShowComposition } from '../components/ShowComposition';

dayjs.extend(utc);
dayjs.extend(timezone);

const HISTORY_WINDOW_MONTHS = 2;

export default function OperationView() {
  const { operationId } = useParams<{ operationId: string }>();
  const history = useHistory();
  const { pathname, search } = useLocation();
  const { auth } = useAuthContext();
  const { setBreadcrumbsItens } = useMonitorContext();

  const [valueType, setValueType] = useState<'percentage' | 'amount'>(
    'percentage',
  );
  const [columnOrder, setColumnOrder] = useState<MRT_ColumnOrderState>([]);
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
    {},
  );
  const [operation, setOperation] = useState<Operation | null>(null);
  const [indicatorName, setIndicatorName] = useState('');
  const [indicatorColumn, setIndicatorColumn] = useState('');
  const [openIndicatorDataModal, setOpenIndicatorDataModal] = useState(false);
  const [openCompositionModal, setOpenCompositionModal] = useState(false);

  useEffect(() => {
    if (!auth?.isLoggedIn) {
      history.replace(buildLoginRedirectPath(pathname, search));
    }
  }, [auth?.isLoggedIn, history, pathname, search]);

  useEffect(() => {
    if (!operationId) {
      history.replace('/monitor');
    }
  }, [operationId, history]);

  const { data: metadata, isLoading: isLoadingMetadata } = useQuery({
    queryKey: ['operation-view-metadata', operationId],
    queryFn: () => getOperationViewMetadata(operationId || ''),
    enabled: !!operationId && Boolean(auth?.isLoggedIn),
    onError: (error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;

      if (status === 403) {
        toast.error('Você não tem permissão para acessar este fundo.');
        return;
      }

      if (status === 400) {
        toast.error('Erro de integração ao carregar os dados do fundo.');
        return;
      }

      toast.error('Não foi possível carregar os dados do fundo.');
      history.replace('/monitor');
    },
    refetchOnWindowFocus: false,
  });

  const { data: compositionRaw, isLoading: isLoadingComposition } = useQuery({
    queryKey: ['operation-composition', operationId],
    queryFn: () => getOperationComposition(operationId || ''),
    enabled: !!operationId && Boolean(auth?.isLoggedIn),
    onError: () => {
      toast.error('Não foi possível carregar a composição do fundo.');
    },
    refetchOnWindowFocus: false,
  });

  const compositionData = useMemo(() => {
    if (!compositionRaw) return [] as CompositionDataItem[];

    const filtered = compositionRaw.filter((item) => item.operations > 0);
    const source = filtered.length > 0 ? filtered : compositionRaw;
    const total = sum(source.map((item) => item.operations));

    return source.map((item) => ({
      _key: item.product,
      quotasAmount: item.operations,
      value: (item.operations / (total || 1)) * 100,
    }));
  }, [compositionRaw]);

  const { data: columnsData, isLoading: isLoadingColumns } = useQuery({
    queryKey: ['operation-indicators-columns', valueType],
    queryFn: () => getIndicatorsColumnsByMarketAndValueType('FIDC', valueType),
    enabled: Boolean(auth?.isLoggedIn),
    staleTime: 1000 * 60 * 60 * 6,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const { data: indicatorsResponseData, isLoading: isLoadingIndicators } =
    useQuery({
      queryKey: [
        'operation-indicators',
        operationId,
        metadata?.latestCompetencyDate ?? undefined,
      ],
      queryFn: () =>
        getOperationIndicators(
          operationId || '',
          metadata?.latestCompetencyDate ?? undefined,
        ),
      onError: () => {
        toast.error('Não foi possível carregar os indicadores do fundo.');
      },
      enabled: !!operationId && !isLoadingColumns && Boolean(auth?.isLoggedIn),
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
    });

  const operationCompetenceDate = useMemo(() => {
    const indicator = indicatorsResponseData?.[0]?.indicators[0];

    return indicator?.document_date
      ? dayjs(indicator.document_date)
          .tz(UQBAR_TIMEZONE)
          .format(UQBAR_DATE_FORMAT)
      : '';
  }, [indicatorsResponseData]);

  const { data: monitorSummary, isLoading: isLoadingMonitorSummary } = useQuery(
    {
      queryKey: [
        'operation-view-summary',
        operationId,
        operationCompetenceDate,
      ],
      queryFn: () =>
        getOperationViewMetadata(operationId || '', operationCompetenceDate),
      enabled:
        !!operationId && !!operationCompetenceDate && Boolean(auth?.isLoggedIn),
      refetchOnWindowFocus: false,
    },
  );

  const isLoadingSummary =
    isLoadingIndicators ||
    (!!operationCompetenceDate && isLoadingMonitorSummary);

  const operationName = metadata?.operation.name ?? '';

  useEffect(() => {
    if (!metadata?.operation.name) return;

    setBreadcrumbsItens([
      { title: 'Monitor', link: '/monitor' },
      { title: 'Visualização', link: `/monitor/wallet/view/${operationId}` },
      {
        title: metadata.operation.name.toUpperCase(),
        link: `/monitor/wallet/view/${operationId}`,
      },
    ]);

    return () => {
      setBreadcrumbsItens([]);
    };
  }, [metadata?.operation.name, operationId, setBreadcrumbsItens]);

  const handleClickIndicatorCell = useCallback(
    (
      clickedOperation: Operation,
      clickedIndicatorName: string,
      clickedIndicatorColumn: string,
    ) => {
      setOperation(clickedOperation);
      setIndicatorName(clickedIndicatorName);
      setIndicatorColumn(clickedIndicatorColumn);
      setOpenIndicatorDataModal(true);
    },
    [],
  );

  const isLoading = isLoadingMetadata || isLoadingColumns;

  const displayOperationId = useMemo(
    () => metadata?.operation.hashId ?? operationId ?? '',
    [metadata?.operation.hashId, operationId],
  );

  if (!auth?.isLoggedIn) {
    return null;
  }

  if (isLoading && !metadata) {
    return (
      <Box
        minHeight={400}
        display="flex"
        alignItems="center"
        justifyContent="center">
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <>
      <Paper component="section" elevation={2}>
        <Box
          p={1}
          borderRadius="5px"
          display="flex"
          flexDirection="column"
          gap={2}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            p={4}
            flexWrap="wrap"
            gap={2}>
            <Typography variant="h5">{operationName.toUpperCase()}</Typography>
            {displayOperationId && operationName && (
              <MonitorFundButton
                operationId={displayOperationId}
                operationName={operationName}
                stepsToShow={['follow', 'monitor']}
              />
            )}
          </Box>

          <FundViewInfoCards
            isLoadingComposition={isLoadingComposition}
            isLoadingSummary={isLoadingSummary}
            compositionData={compositionData}
            fundsCount={monitorSummary?.summary.fundsCount ?? 0}
            triggeredAlertsCount={
              monitorSummary?.summary.triggeredAlertsCount ?? 0
            }
            onOpenComposition={() => setOpenCompositionModal(true)}
          />

          <Box display="flex" flexDirection="column" gap={0}>
            <TabNavigation value={valueType} handleSelectTab={setValueType} />
            <DynamicIndicatorsTable
              viewMode="operation"
              columnOrder={columnOrder}
              setColumnOrder={setColumnOrder}
              columnVisibility={columnVisibility}
              setColumnVisibility={setColumnVisibility}
              isLoadingColumns={isLoadingColumns}
              isLoadingIndicators={isLoadingIndicators}
              indicatorsResponseData={indicatorsResponseData ?? []}
              columnsData={columnsData}
              walletId={0}
              valueType={valueType}
              handleIndicatorClick={handleClickIndicatorCell}
              competenceDate={operationCompetenceDate}
            />
          </Box>
        </Box>
      </Paper>

      <ShowComposition
        compositions={compositionData}
        open={openCompositionModal}
        onClose={() => setOpenCompositionModal(false)}
        onFinish={() => setOpenCompositionModal(false)}
      />

      <IndicatorDetailsModal
        open={openIndicatorDataModal}
        onClose={() => setOpenIndicatorDataModal(false)}
        targetDate={operationCompetenceDate}
        document={operation?.cnpj || metadata?.operation.cnpj || ''}
        indicatorName={indicatorName}
        indicatorColumn={indicatorColumn}
        historyWindowMonths={HISTORY_WINDOW_MONTHS}
        hideWindowNavigation
      />
    </>
  );
}
